import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom, useAtomValue } from 'jotai'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Callout, Input2FA, RowsSkeletonLoader } from '@node-space/ui-components'
import { TWO_FA_REQUIRED_LENGTH } from 'components/TwoFA/TwoFAField'
import { useCreateContactBankAccount } from 'hooks/contacts/useCreateContactBankAccount'
import { useContactMigrationBeneficiaryUpdate } from 'hooks/useContactMigrationBeneficiaryUpdate'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import {
  contactActionAtom,
  contactAtom,
  contactInstrumentActionAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { FormFieldLabels } from 'pages/Contacts/types/fiatInstruments'
import {
  getConfirmPayeeBeneficiaryParams,
  mapFormDataToBeneficiaryDetails,
} from 'pages/Contacts/utils/fiatInstruments'
import { ConfirmationOfPayeeCallout } from 'pages/Wallets/components/FiatSend/components/ConfirmationOfPayeeCallout'
import { useConfirmBeneficiaryCallout } from 'pages/Wallets/components/FiatSend/hooks/useConfirmBeneficiaryCallout'
import { ConfirmationOfPayeeCalloutTypes } from 'pages/Wallets/components/FiatSend/types'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { ContactActionIds } from 'types/atoms/atom.contacts'
import { Address, BeneficiaryDetailsCurrency, PaymentMethods } from 'types/beneficiaries'
import { ContactPaymentInstrument } from 'types/contacts'
import { ConfirmPayeeBeneficiaryParams } from 'types/payoutsV2'
import { getAddress } from 'utils/beneficiaries'
import { ContactsSheets } from '../ContactsSheets'
import { BankDetailsConfirmationCallout } from './BankDetailsConfirmationCallout'
import { FiatInstrumentConfirmationDataListItems } from './FiatInstrumentConfirmationDataListItems'
import { selectedContactBankAccountAtom } from 'pages/Wallets/components/Pay/atoms/atoms'
import { getMappedUpdatedPaymentInstrument } from 'pages/Wallets/components/FiatSend/utils'

type FiatInstrumentConfirmationProps = {
  formFieldLabels: FormFieldLabels
  formValues: ContactFiatInstrumentFormData
  isLoading: boolean
  form: UseFormReturn<ContactFiatInstrumentFormData>
}

export const FiatInstrumentConfirmation = ({
  formFieldLabels,
  formValues,
  isLoading,
  form,
}: FiatInstrumentConfirmationProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { twoFactorEnabled } = useTwoFAContext()

  const contact = useAtomValue(contactAtom)
  const [contactAction, setContactAction] = useAtom(contactActionAtom)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)
  const [contactBankAccount, setContactBankAccount] = useAtom(selectedContactBankAccountAtom)

  const isSWIFT = formValues?.paymentMethods?.includes(PaymentMethods.SWIFT)
  const isUsd = formValues?.currency === BeneficiaryDetailsCurrency.USD
  const isZar = formValues?.currency === BeneficiaryDetailsCurrency.ZAR
  const isEur = formValues?.currency === BeneficiaryDetailsCurrency.EUR
  const isAccountTypeRequired = isUsd || isZar

  const [isBankDetailsConfirmed, setIsBankDetailsConfirmed] = useState(isEur)
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)

  useEffect(() => {
    form?.resetField('twoFactorCode', { keepTouched: false, defaultValue: '' })
    form?.setValue('twoFactorCode', '')
  }, [form])

  const { mutate: updateBeneficiaryAsMigrated } = useContactMigrationBeneficiaryUpdate()

  const {
    onSubmitBankAccount,
    isLoading: isSubmittingBankAccount,
    calloutError,
  } = useCreateContactBankAccount({
    contact,
    formValues,
    form,
    onSubmit2FASuccess: (paymentInstrument: ContactPaymentInstrument) => {
      if (contactAction?.meta?.contactBeneficiary) {
        updateBeneficiaryAsMigrated(
          {
            beneficiary: contactAction?.meta?.contactBeneficiary,
            instrumentId: paymentInstrument?.id,
          },
          {
            onSettled: () => {
              queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACT_INSTRUMENTS] })
              queryClient.invalidateQueries({ queryKey: [reactQueryName.BENEFICIARIES] })
              queryClient.invalidateQueries({ queryKey: [reactQueryName.WALLET_CONTACT_PAYMENT_INSTRUMENTS] })

              contactInstrumentAction?.onClose?.()
              setContactInstrumentAction(null)
            },
          }
        )
      } else {
        if (contactInstrumentAction?.meta?.keepUpdatedPaymentInstrument) {
          const updatedPaymentInstrument = getMappedUpdatedPaymentInstrument(contactBankAccount, paymentInstrument)
          contactInstrumentAction?.meta?.onPaymentInstrumentUpdate?.(updatedPaymentInstrument)
          setContactBankAccount(updatedPaymentInstrument)
        }
        contactInstrumentAction?.onClose?.()
        setContactInstrumentAction(null)
      }
    },
  })

  const beneficiaryDetails: ConfirmPayeeBeneficiaryParams = useMemo(() => {
    const beneficiary = mapFormDataToBeneficiaryDetails(contact, formValues)
    return getConfirmPayeeBeneficiaryParams(beneficiary)
  }, [contact, formValues])

  const { confirmPayeeData, confirmationOfPayeeCallout, isConfirmingPayee, editBeneficiary } =
    useConfirmBeneficiaryCallout({
      beneficiaryDetails,
      isPayout: false,
      isContact: true,
    })

  useEffect(() => {
    if (formValues?.twoFactorCode?.length === TWO_FA_REQUIRED_LENGTH && twoFactorEnabled) {
      onSubmitBankAccount?.()
    }
  }, [formValues?.twoFactorCode, twoFactorEnabled])


  const bankAddressData: Address = {
    addressLine1: formValues?.bankAddressLine1,
    addressLine2: formValues?.bankAddressLine2,
    city: formValues?.bankCity,
    postCode: formValues?.bankPostCode,
    region: formValues?.bankRegion,
    country: formValues?.bankCountryName || formValues?.bankCountryCode,
  }

  const intermediaryBankAddressData: Address = {
    addressLine1: formValues?.intermediaryBankAddressLine1,
    addressLine2: formValues?.intermediaryBankAddressLine2,
    city: formValues?.intermediaryBankCity,
    postCode: formValues?.intermediaryBankPostCode,
    region: formValues?.intermediaryBankRegion,
    country: formValues?.intermediaryBankCountryCode,
  }

  const beneficiaryAddressData: Address = {
    addressLine1: formValues?.beneficiaryAddressLine1,
    addressLine2: formValues?.beneficiaryAddressLine2,
    city: formValues?.beneficiaryCity,
    postCode: formValues?.beneficiaryPostCode,
    region: formValues?.beneficiaryRegion,
    country: formValues?.beneficiaryCountryName || formValues?.beneficiaryCountryCode,
  }

  const beneficiaryAddress = getAddress(beneficiaryAddressData)
  const bankAddress = getAddress(bankAddressData)
  const intermediaryBankAddress = getAddress(intermediaryBankAddressData)

  const handleBankDetailsConfirmationChange = (isChecked: boolean) => {
    const isSwift = formValues?.paymentMethods?.includes(PaymentMethods.SWIFT)
    setIsBankDetailsConfirmed(isSwift ? true : isChecked)
  }

  const handleOn2FAChange = (value: string) => {
    form.setValue('twoFactorCode', value)
  }

  const handleEditContact = useCallback(() => {
    setContactAction({
      ...contactAction,
      actionId: ContactActionIds.CONTACT,
      header: t('contacts.addContact'),
      onClose: () => {
        setIsContactSheetOpen(false)
        contactAction?.onClose?.()
      },
      meta: {
        keepUpdatedContact: true,
      },
    })
    setIsContactSheetOpen(true)
  }, [setContactAction])

  const confirmationDetailsDisabled = useMemo(() => {
    if (isEur) {
      return !isBankDetailsConfirmed && !isSWIFT
    }

    return !isBankDetailsConfirmed
  }, [isEur, isBankDetailsConfirmed, isSWIFT])

  return isConfirmingPayee ? (
    <Box paddingT={24}>
      <RowsSkeletonLoader rows={6} />
    </Box>
  ) : (
    <Box width="full" paddingT={12}>
      {!!calloutError && (
        <Box paddingB={12}>
          <Callout variant="error">{calloutError}</Callout>
        </Box>
      )}
      <FiatInstrumentConfirmationDataListItems
        formFieldLabels={formFieldLabels}
        formValues={formValues}
        beneficiaryAddress={beneficiaryAddress}
        bankAddress={bankAddress}
        intermediaryBankAddress={intermediaryBankAddress}
        isAccountTypeRequired={isAccountTypeRequired}
        isSWIFT={isSWIFT}
      />

      <Box flexCol gap={12}>
        {confirmationOfPayeeCallout && (
          <ConfirmationOfPayeeCallout
            type={ConfirmationOfPayeeCalloutTypes.BANK_ACCOUNT}
            confirmationOfPayeeCallout={confirmationOfPayeeCallout}
            confirmPayeeData={confirmPayeeData}
            editBeneficiary={editBeneficiary}
            handleEditBeneficiary={handleEditContact}
          />
        )}
        {!isEur && (
          <BankDetailsConfirmationCallout
            onDetailsConfirmationChange={handleBankDetailsConfirmationChange}
          />
        )}
      </Box>

      <Box width="full" paddingB={24} paddingT={24}>
        <Input2FA
          error={form.formState?.errors?.twoFactorCode?.message}
          label={t('authCodeConfirm')}
          testId="add-fiat-instrument-2fa"
          disabled={confirmationDetailsDisabled}
          loading={isLoading || isSubmittingBankAccount}
          onChange={handleOn2FAChange}
          value={formValues?.twoFactorCode}
        />
      </Box>
      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet />
    </Box>
  )
}
